// clients/laid-back-cycles/shopify/theme/assets/configurator-engine.js
/*
 * LBC Configurator Engine — pure, framework-free build logic.
 * Loaded in the theme as a plain <script> (attaches window.LBCConfiguratorEngine)
 * and required directly by node:test (module.exports). No DOM, no Shopify deps.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  else { root.LBCConfiguratorEngine = api; }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function optionIndex(config) {
    var idx = {};
    config.groups.forEach(function (g) {
      g.options.forEach(function (o) { idx[o.id] = g.id; });
    });
    return idx;
  }

  function rulesFor(config, optionId) {
    return config.rules.filter(function (r) { return r.option === optionId; });
  }

  /*
   * resolve(): take a raw {groupId: optionId} selection and return
   *   { selection: sanitized (auto-resolved), disabled: {optionId: reasonOptionId} }
   * Semantics (deterministic, manufacturer-faithful), applied to a fixpoint:
   *   - requires: a selected option forces its required option's group to that option.
   *   - excludes: an option excluded by ANY selected option is disabled; if it was
   *     selected, its group falls back to the first non-disabled option.
   *   - show_when: a group carrying a show_when array is HIDDEN unless one of those
   *     option ids is currently selected; a hidden group is reset to its default so
   *     a stale (e.g. price-altering) selection cannot linger off-screen.
   *   - hide_when: a group carrying a hide_when array is HIDDEN whenever one of those
   *     option ids IS selected (the inverse of show_when — used when a sibling choice
   *     supersedes a whole step, e.g. a hub gear that bundles its own wheel pack +
   *     shifter, so the derailleur wheel-pack/shifter steps no longer apply). Also
   *     reset to default so the off-screen selection can't skew totals.
   * Returns { selection, disabled, hidden } where hidden is { groupId: true }.
   */
  function resolve(config, rawSelection) {
    var idx = optionIndex(config);
    var selection = {};
    config.groups.forEach(function (g) {
      selection[g.id] = (rawSelection && rawSelection[g.id]) || g.default || g.options[0].id;
    });

    var disabled = {};
    for (var pass = 0; pass < config.groups.length + 2; pass++) {
      disabled = {};
      var changed = false;

      config.groups.forEach(function (g) {
        rulesFor(config, selection[g.id]).forEach(function (r) {
          (r.requires || []).forEach(function (req) {
            var gid = idx[req];
            if (gid && selection[gid] !== req) { selection[gid] = req; changed = true; }
          });
        });
      });

      config.groups.forEach(function (g) {
        var sel = selection[g.id];
        rulesFor(config, sel).forEach(function (r) {
          (r.excludes || []).forEach(function (ex) { disabled[ex] = sel; });
        });
      });

      config.groups.forEach(function (g) {
        if (disabled[selection[g.id]]) {
          var fallback = g.options.filter(function (o) { return !disabled[o.id]; })[0];
          if (fallback && fallback.id !== selection[g.id]) {
            selection[g.id] = fallback.id; changed = true;
          }
        }
      });

      if (!changed) break;
    }

    // Whole-group visibility: a group with show_when is hidden unless one of its
    // trigger options is selected. Hidden groups reset to their default so an
    // off-screen selection can't skew totalPrice/encode/summary.
    var hidden = {};
    config.groups.forEach(function (g) {
      var hide = false;
      if (g.show_when) {
        var visible = g.show_when.some(function (trigger) {
          var gid = idx[trigger];
          return gid && selection[gid] === trigger;
        });
        if (!visible) { hide = true; }
      }
      if (!hide && g.hide_when) {
        var suppressed = g.hide_when.some(function (trigger) {
          var gid = idx[trigger];
          return gid && selection[gid] === trigger;
        });
        if (suppressed) { hide = true; }
      }
      if (hide) {
        hidden[g.id] = true;
        selection[g.id] = g.default || g.options[0].id;
      }
    });

    return { selection: selection, disabled: disabled, hidden: hidden };
  }

  function select(config, selection, groupId, optionId) {
    var raw = {};
    config.groups.forEach(function (g) { raw[g.id] = selection[g.id]; });
    raw[groupId] = optionId;
    return resolve(config, raw).selection;
  }

  function defaultSelection(config) {
    return resolve(config, {}).selection;
  }

  function totalPrice(config, selection) {
    var total = config.base_price || 0;
    config.groups.forEach(function (g) {
      var optId = selection[g.id];
      var opt = g.options.filter(function (o) { return o.id === optId; })[0];
      if (opt) { total += (opt.price_delta || 0); }
    });
    return total;
  }

  function encodeSelection(config, selection) {
    return config.groups.map(function (g) { return selection[g.id]; }).join('.');
  }

  function decodeSelection(config, str) {
    var idx = optionIndex(config);
    var raw = {};
    (str || '').split('.').forEach(function (tok) {
      var gid = idx[tok];
      if (gid) { raw[gid] = tok; }
    });
    return resolve(config, raw).selection;
  }

  // Plain-English summary for the result screen + DTS hand-off.
  function summary(config, selection) {
    var lines = config.groups.map(function (g) {
      var opt = g.options.filter(function (o) { return o.id === selection[g.id]; })[0];
      return g.label + ': ' + (opt ? opt.label : '—');
    });
    return lines;
  }

  return {
    optionIndex: optionIndex,
    resolve: resolve,
    select: select,
    defaultSelection: defaultSelection,
    totalPrice: totalPrice,
    encodeSelection: encodeSelection,
    decodeSelection: decodeSelection,
    summary: summary
  };
});
